import React, { useCallback, useEffect, useState } from 'react';
import { applyToOpening, listCandidateOpenings, listCandidateApplications } from '../../api/candidatePortal';

export default function CandidateOpeningsPage() {
  const [openings, setOpenings] = useState([]);
  const [appliedJobIds, setAppliedJobIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [applying, setApplying] = useState(null);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [resOpenings, resApps] = await Promise.all([
        listCandidateOpenings({
          keyword: keyword || undefined,
          location: location || undefined,
          employmentType: employmentType || undefined,
        }),
        listCandidateApplications().catch(() => ({ data: [] })),
      ]);

      const apps = Array.isArray(resApps.data) ? resApps.data : [];
      const appliedIds = new Set(
        apps.map((a) => a.jobOpeningId || a.openingId || a.id).filter(Boolean)
      );
      setAppliedJobIds(appliedIds);

      // Real openings only (no prototype jobs a candidate could try to apply to).
      const rawList = Array.isArray(resOpenings.data) ? resOpenings.data : [];

      setOpenings(rawList);
    } catch (err) {
      console.warn('[Openings]', err.message);
    } finally {
      setLoading(false);
    }
  }, [keyword, location, employmentType]);

  useEffect(() => { load(); }, [load]);

  const onApply = async (id) => {
    setApplying(id);
    setMessage('');
    setMessageIsError(false);
    try {
      await applyToOpening(id);
      setAppliedJobIds((prev) => new Set([...prev, id]));
      setMessage('Application submitted! Check My Applications to track status.');
    } catch (err) {
      // A failed apply must never be reported as submitted.
      setMessageIsError(true);
      setMessage(err.data?.message || 'Could not submit your application. Please try again.');
    } finally {
      setApplying(null);
    }
  };

  const getMatchChip = (job) => {
    if (job.matchLabel) {
      return (
        <span className={`chip ${job.matchType === 'amber' ? 'chip-amber' : 'chip-teal'}`}>
          {job.matchLabel}
        </span>
      );
    }
    if (job.matchScore != null) {
      const cls = job.matchScore >= 80 ? 'chip-teal' : job.matchScore >= 60 ? 'chip-teal' : 'chip-amber';
      const label = job.matchScore >= 80 ? 'Strong match' : job.matchScore >= 60 ? 'Good match' : 'Entry level';
      return <span className={`chip ${cls}`}>{label}</span>;
    }
    return <span className="chip chip-teal">Good match</span>;
  };

  return (
    <div className="space-y-6">
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div>
        <h1
          className="text-2xl sm:text-[26px] font-semibold text-[var(--navy-950)] tracking-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Job Openings
        </h1>
        <p className="text-[13.5px] text-[var(--ink-soft)] mt-1">
          Browse open roles across companies hiring on Samastix.
        </p>
      </div>

      {/* ── Search & Filter Bar ───────────────────────────────────────── */}
      <div className="card p-3 sm:p-4">
        <div className="grid sm:grid-cols-12 gap-3 items-center">
          <div className="sm:col-span-5 relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-faint)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search by role, skill, or keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="input !pl-10 !py-2 !text-xs sm:!text-sm"
            />
          </div>

          <div className="sm:col-span-4 relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-faint)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="input !pl-10 !py-2 !text-xs sm:!text-sm"
            />
          </div>

          <div className="sm:col-span-3">
            <select
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value)}
              className="input !py-2 !text-xs sm:!text-sm bg-white"
            >
              <option value="">All types</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
            </select>
          </div>
        </div>
      </div>

      {message && (
        <div className={`p-3.5 rounded-[10px] border text-xs font-medium flex items-center justify-between ${messageIsError ? 'bg-[var(--rose-100)] border-[var(--rose-100)] text-[var(--rose-600)]' : 'bg-[var(--teal-050)] border-[var(--teal-100)] text-[var(--teal-700)]'}`} role={messageIsError ? 'alert' : 'status'}>
          <span>{message}</span>
          <button
            type="button"
            onClick={() => setMessage('')}
            className="text-[var(--teal-600)] hover:text-[var(--teal-700)] font-bold ml-3"
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-24 text-center">
          <div className="w-8 h-8 border-2 border-[var(--teal-500)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[var(--ink-soft)]">Loading openings…</p>
        </div>
      ) : !openings.length ? (
        <div className="card p-12 text-center text-[var(--ink-soft)]">
          <p className="text-sm">No open roles match your filters.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {openings.map((job) => {
            const isApplied = job.applied || appliedJobIds.has(job.id);
            const tags = job.tags || [
              job.employmentType || 'Full-time',
              ...(job.department ? [job.department] : ['Engineering']),
            ];

            return (
              <div
                key={job.id}
                className="card p-5 sm:p-6 card-hover flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3
                        className="text-base font-semibold text-[var(--navy-950)]"
                        style={{ fontFamily: 'var(--font-heading)' }}
                      >
                        {job.title}
                      </h3>
                      <p className="text-xs text-[var(--ink-soft)] mt-0.5">
                        {job.orgName || 'Samastix Partner'} · {job.location || 'Remote'}
                      </p>
                    </div>
                    {getMatchChip(job)}
                  </div>

                  {/* Skills/Tags */}
                  <div className="flex flex-wrap gap-1.5 mt-3.5">
                    {tags.map((tag, tIdx) => (
                      <span
                        key={tIdx}
                        className="chip chip-navy !text-[11px] !py-0.5 !px-2.5 font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {job.description && (
                    <p className="text-xs text-[var(--ink-soft)] mt-3 line-clamp-2 leading-relaxed">
                      {job.description}
                    </p>
                  )}
                </div>

                {/* Bottom Row: Posted date + Action Button */}
                <div className="mt-5 pt-4 border-t border-[var(--line-soft)] flex items-center justify-between">
                  <span className="text-xs text-[var(--ink-faint)] font-tabular">
                    {job.postedAgo || 'Posted recently'}
                  </span>

                  {isApplied ? (
                    <span className="btn btn-ghost !py-1.5 !px-4 !text-xs !cursor-default !text-[var(--ink-faint)]">
                      Applied
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onApply(job.id)}
                      disabled={applying === job.id}
                      className="btn btn-primary !py-1.5 !px-5 !text-xs"
                    >
                      {applying === job.id ? 'Applying…' : 'Apply'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
